import { useState, useCallback, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { updateSearchTerms } from "../../reducers/resourcesList/search";
import { useThrottledCallback } from "../Utils/useThrottledCallback";
import Icon from "../Utils/Icon";
import { useFormatMessage } from "../Intl/Context";
import { useResourcesList } from "../ResourcesList/resourcesList";
import { useUniqueId } from "../Utils/uniqueId";
import { isUndefined } from "lodash";

const Search = props => {
  const dispatch = useDispatch();

  const t = useFormatMessage();
  const label = t(intlMessages.label);
  const srDescription = props.srDescription || t(intlMessages.srDescription);

  const { resourcesListKey, search } = useResourcesList();
  const { terms } = search;
  const [inputValue, setInputValue] = useState(terms);
  const { disableThrottling, enableLabel } = props;

  const previousSearchValueRef = useRef(terms);

  // This allows making the input's value react to external updates, like filters
  // resetting, while letting input's local state managed value to react to user's input
  const value = useMemo(() => {
    const previousSearchValue = previousSearchValueRef.current;

    if (
      // Nothing to do if search and local states are the same
      terms === inputValue ||
      // Nothing to do if the search throttling callback as already been called
      previousSearchValue === terms ||
      // Nothing to do if the filter was just reset and the terms where reset to undefined
      isUndefined(previousSearchValue)
    ) {
      return inputValue;
    }

    previousSearchValueRef.current = terms;
    return terms;
  }, [inputValue, terms]);

  const labelId = useUniqueId("search-label-");
  const inputId = useUniqueId("search-input-");

  const className = classNames(["search-filter", "search-bar", "w-100", props.className]);

  const throttledDelay = disableThrottling ? 0 : 1000;

  // This input's throttling allows for reducing server requests while the user types,
  // but also reduces rendering issues if multiple requests are fetching at the same time,
  // which seems to suspend the whole contributions visualization component instead of
  // just suspending the filters loader.
  const throttledSearchTermsUpdate = useThrottledCallback(
    value => {
      previousSearchValueRef.current = value;
      dispatch(updateSearchTerms(resourcesListKey, value));
    },
    [dispatch, resourcesListKey],
    throttledDelay,
    {
      leading: false,
      trailing: true
    }
  );

  const handleInputChanged = useCallback(
    ({ currentTarget }) => {
      const { value } = currentTarget;
      setInputValue(value);
      throttledSearchTermsUpdate(value);
    },
    [throttledSearchTermsUpdate]
  );

  return (
    <div className={className}>
      {enableLabel && (
        <label id={labelId} htmlFor={inputId}>
          {label}
          <span className="sr-only">{srDescription}</span>
        </label>
      )}
      <div className="input-group bg-gray align-items-center">
        <span className="input-group-addon padding-x-xs line-height-none">
          <Icon className="icon-magnify" />
        </span>
        <input
          id={inputId}
          className="form-control"
          type="text"
          onChange={handleInputChanged}
          value={value}
          title={label}
          placeholder={label}
          aria-describedby={enableLabel && labelId}
        />
        {!enableLabel && (
          <span id={labelId} className="sr-only">
            {srDescription}
          </span>
        )}
      </div>
    </div>
  );
};

export default Search;

const intlMessages = defineMessages({
  label: {
    id: "filters.search.label",
    defaultMessage: "Rechercher"
  },
  srDescription: {
    id: "filters.search.srDescription",
    defaultMessage:
      "les résultats de recherche sont mis à jour en temps réel au fur et à mesure de votre saisie"
  }
});
